import React, { useEffect, useState } from 'react';
import { Plus, Shield, Edit2, Trash2, X, Users, Lock, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw } from 'lucide-react';
import { useWorkspace, Permission, permissionCategories, allPermissions } from '../contexts/WorkspaceContext';
import { rolesApi, usersApi, RoleRecord, RolePermissionRow, UserRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function RolesPermissions() {
  const {
    currentUser,
    refreshRoles
  } = useWorkspace();
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(Object.keys(permissionCategories)));
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center" data-id="element-3915">
        <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-3916" />
        <h2 className="mt-4 text-lg font-medium text-gray-900" data-id="element-3917">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-500" data-id="element-3918">
          Only Super Admins can manage roles and permissions.
        </p>
      </div>;
  }
  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesData, usersData] = await Promise.all([rolesApi.list(), usersApi.list()]);
      setRoles(rolesData);
      setAllUsers(usersData);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) newExpanded.delete(category);else newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };
  const togglePermission = (permission: string, isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => ({
      ...prev,
      permissions: prev.permissions.includes(permission) ? prev.permissions.filter((p: string) => p !== permission) : [...prev.permissions, permission]
    }));
  };
  const toggleAllInCategory = (perms: string[], isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => {
      const allSelected = perms.every(p => prev.permissions.includes(p));
      return {
        ...prev,
        permissions: allSelected ? prev.permissions.filter((p: string) => !perms.includes(p)) : [...new Set([...prev.permissions, ...perms])]
      };
    });
  };
  const handleCreateRole = async () => {
    if (!newRole.name.trim()) return;
    setIsSaving(true);
    try {
      const permRows: RolePermissionRow[] = newRole.permissions.map(p => ({
        Permission_Key: p
      }));
      await rolesApi.create({
        Role_Name: newRole.name,
        Description: newRole.description,
        Is_System: false,
        Role_Permissions: permRows
      });
      toast.success('Role created');
      setNewRole({
        name: '',
        description: '',
        permissions: []
      });
      setIsCreating(false);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to create role:', err);
      toast.error('Failed to create role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleStartEdit = (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role) {
      setEditForm({
        name: role.Role_Name,
        description: role.Description || '',
        permissions: (role.Role_Permissions || []).map(p => p.Permission_Key)
      });
      setEditingRole(roleId);
    }
  };
  const handleSaveEdit = async () => {
    if (!editingRole || !editForm.name.trim()) return;
    setIsSaving(true);
    try {
      const permRows: RolePermissionRow[] = editForm.permissions.map(p => ({
        Permission_Key: p
      }));
      await rolesApi.update(editingRole, {
        Role_Name: editForm.name,
        Description: editForm.description,
        Role_Permissions: permRows
      });
      toast.success('Role updated');
      setEditingRole(null);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error('Failed to update role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteRole = async (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role?.Is_System) {
      toast.error('Cannot delete system roles');
      return;
    }
    if (!confirm(`Delete role "${role?.Role_Name}"?`)) return;
    try {
      await rolesApi.delete(roleId);
      toast.success('Role deleted');
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      toast.error('Failed to delete role');
    }
  };
  const handleDuplicateRole = async (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (!role) return;
    setIsSaving(true);
    try {
      await rolesApi.create({
        Role_Name: `${role.Role_Name} (Copy)`,
        Description: role.Description,
        Is_System: false,
        Role_Permissions: role.Role_Permissions || []
      });
      toast.success('Role duplicated');
      loadData();
      refreshRoles();
    } catch (err) {
      toast.error('Failed to duplicate role');
    } finally {
      setIsSaving(false);
    }
  };
  const getUsersWithRole = (roleName: string) => {
    return allUsers.filter(u => u.Role === roleName);
  };
  const formatPermissionName = (permission: string) => {
    const parts = permission.split('.');
    const action = parts[parts.length - 1];
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const renderPermissionEditor = (currentPermissions: string[], isEdit: boolean) => <div className="space-y-2" data-id="element-3919">
      {Object.entries(permissionCategories).map(([category, perms]) => {
      const isExpanded = expandedCategories.has(category);
      const selectedCount = perms.filter(p => currentPermissions.includes(p)).length;
      const allSelected = selectedCount === perms.length;
      return <div key={category} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3920">
            <button type="button" onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3921">
              <div className="flex items-center gap-3" data-id="element-3922">
                {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-3923" /> : <ChevronRight className="h-4 w-4 text-gray-500" data-id="element-3924" />}
                <span className="font-medium text-gray-900" data-id="element-3925">{category}</span>
                <span className="text-xs text-gray-500" data-id="element-3926">
                  ({selectedCount}/{perms.length})
                </span>
              </div>
              <button type="button" onClick={e => {
            e.stopPropagation();
            toggleAllInCategory(perms, isEdit);
          }} className={`text-xs px-2 py-1 rounded ${allSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'} hover:opacity-80`} data-id="element-3927">
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </button>
            {isExpanded && <div className="p-4 grid grid-cols-2 gap-2" data-id="element-3928">
                {perms.map(perm => <label key={perm} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-id="element-3929">
                    <input type="checkbox" checked={currentPermissions.includes(perm)} onChange={() => togglePermission(perm, isEdit)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-3930" />
                    <span className="text-sm text-gray-700" data-id="element-3931">
                      {formatPermissionName(perm)}
                    </span>
                  </label>)}
              </div>}
          </div>;
    })}
    </div>;
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-3932">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3933" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-3934">
      <div className="flex items-center justify-between" data-id="element-3935">
        <div data-id="element-3936">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3937">
            Roles & Permissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3938">
            Manage user roles and their access permissions.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3939">
          <button onClick={loadData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-3940">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3941" />
          </button>
          <button onClick={() => setIsCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3942">
            <Plus className="h-4 w-4" data-id="element-3943" />
            Create Role
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-3944">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3945">
          <div className="flex items-center gap-3" data-id="element-3946">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-3947">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-3948" />
            </div>
            <div data-id="element-3949">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-3950">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3951">Total Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3952">
          <div className="flex items-center gap-3" data-id="element-3953">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-3954">
              <Lock className="h-5 w-5 text-blue-600" data-id="element-3955" />
            </div>
            <div data-id="element-3956">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-3957">
                {roles.filter(r => r.Is_System).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3958">System Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3959">
          <div className="flex items-center gap-3" data-id="element-3960">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-3961">
              <Users className="h-5 w-5 text-green-600" data-id="element-3962" />
            </div>
            <div data-id="element-3963">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-3964">
                {allPermissions.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3965">Available Permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      {isCreating && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3966">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3967">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsCreating(false)} data-id="element-3968" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-3969">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-3970">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3971">
                  Create New Role
                </h3>
                <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-500" data-id="element-3972">
                  <X className="h-5 w-5" data-id="element-3973" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-3974">
                <div data-id="element-3975">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3976">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.name} onChange={e => setNewRole({
                ...newRole,
                name: e.target.value
              })} placeholder="e.g., Content Manager" data-id="element-3977" />
                </div>
                <div data-id="element-3978">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3979">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.description} onChange={e => setNewRole({
                ...newRole,
                description: e.target.value
              })} placeholder="Brief description..." data-id="element-3980" />
                </div>
                <div data-id="element-3981">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3982">
                    Permissions
                  </label>
                  {renderPermissionEditor(newRole.permissions, false)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-3983">
                <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3984">
                  Cancel
                </button>
                <button onClick={handleCreateRole} disabled={!newRole.name.trim() || isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3985">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3986" />}
                  Create Role
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Role Modal */}
      {editingRole && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3987">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3988">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditingRole(null)} data-id="element-3989" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-3990">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-3991">
                <div data-id="element-3992">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3993">
                    Edit Role
                  </h3>
                  {roles.find(r => r.ID === editingRole)?.Is_System && <p className="text-xs text-purple-600 flex items-center gap-1 mt-1" data-id="element-3994">
                      <Lock className="h-3 w-3" data-id="element-3995" /> System role — name cannot be
                      changed
                    </p>}
                </div>
                <button onClick={() => setEditingRole(null)} className="text-gray-400 hover:text-gray-500" data-id="element-3996">
                  <X className="h-5 w-5" data-id="element-3997" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-3998">
                <div data-id="element-3999">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4000">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" value={editForm.name} onChange={e => setEditForm({
                ...editForm,
                name: e.target.value
              })} disabled={roles.find(r => r.ID === editingRole)?.Is_System} data-id="element-4001" />
                </div>
                <div data-id="element-4002">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4003">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={editForm.description} onChange={e => setEditForm({
                ...editForm,
                description: e.target.value
              })} data-id="element-4004" />
                </div>
                <div data-id="element-4005">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4006">
                    Permissions
                  </label>
                  {renderPermissionEditor(editForm.permissions, true)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-4007">
                <button onClick={() => setEditingRole(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4008">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4009">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4010" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4011">
        {roles.map(role => {
        const roleUsers = getUsersWithRole(role.Role_Name);
        const isSelected = selectedRole === role.ID;
        const permCount = role.Role_Permissions?.length || 0;
        return <div key={role.ID} className={`bg-white border rounded-lg overflow-hidden transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4012">
              <div className="p-4 cursor-pointer" onClick={() => setSelectedRole(isSelected ? null : role.ID)} data-id="element-4013">
                <div className="flex items-start justify-between mb-3" data-id="element-4014">
                  <div className="flex items-center gap-2" data-id="element-4015">
                    <div className={`p-2 rounded-lg ${role.Is_System ? 'bg-purple-50' : 'bg-blue-50'}`} data-id="element-4016">
                      <Shield className={`h-5 w-5 ${role.Is_System ? 'text-purple-600' : 'text-blue-600'}`} data-id="element-4017" />
                    </div>
                    <div data-id="element-4018">
                      <h3 className="font-medium text-gray-900" data-id="element-4019">
                        {role.Role_Name}
                      </h3>
                      {role.Is_System && <span className="inline-flex items-center gap-1 text-xs text-purple-600" data-id="element-4020">
                          <Lock className="h-3 w-3" data-id="element-4021" /> System
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-4022">
                    <button onClick={e => {
                  e.stopPropagation();
                  handleDuplicateRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Duplicate" data-id="element-4023">
                      <Copy className="h-4 w-4" data-id="element-4024" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  handleStartEdit(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-4025">
                      <Edit2 className="h-4 w-4" data-id="element-4026" />
                    </button>
                    {!role.Is_System && <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4027">
                        <Trash2 className="h-4 w-4" data-id="element-4028" />
                      </button>}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2" data-id="element-4029">
                  {role.Description}
                </p>
                <div className="flex items-center justify-between text-sm" data-id="element-4030">
                  <span className="text-gray-500" data-id="element-4031">
                    <span className="font-medium text-gray-900" data-id="element-4032">
                      {permCount}
                    </span>{' '}
                    permissions
                  </span>
                  <span className="text-gray-500" data-id="element-4033">
                    <span className="font-medium text-gray-900" data-id="element-4034">
                      {roleUsers.length}
                    </span>{' '}
                    users
                  </span>
                </div>
              </div>

              {isSelected && <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3" data-id="element-4035">
                  <div data-id="element-4036">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-4037">
                      Permissions by Category
                    </h4>
                    <div className="space-y-1" data-id="element-4038">
                      {Object.entries(permissionCategories).map(([category, perms]) => {
                  const rolePerms = (role.Role_Permissions || []).map(p => p.Permission_Key);
                  const count = perms.filter(p => rolePerms.includes(p)).length;
                  if (count === 0) return null;
                  return <div key={category} className="flex items-center justify-between text-sm" data-id="element-4039">
                              <span className="text-gray-600" data-id="element-4040">{category}</span>
                              <span className={`font-medium ${count === perms.length ? 'text-green-600' : 'text-blue-600'}`} data-id="element-4041">
                                {count}/{perms.length}
                              </span>
                            </div>;
                })}
                    </div>
                  </div>
                  {roleUsers.length > 0 && <div data-id="element-4042">
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-4043">
                        Users with this role
                      </h4>
                      <div className="flex flex-wrap gap-1" data-id="element-4044">
                        {roleUsers.slice(0, 5).map(user => <span key={user.ID} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-200 text-gray-700" data-id="element-4045">
                            {user.User_Name}
                          </span>)}
                        {roleUsers.length > 5 && <span className="text-xs text-gray-500" data-id="element-4046">
                            +{roleUsers.length - 5} more
                          </span>}
                      </div>
                    </div>}
                </div>}
            </div>;
      })}
      </div>

      {roles.length === 0 && <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4047">
          <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-4048" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4049">
            No roles defined
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4050">
            Create roles in Creator or click "Create Role" above.
          </p>
        </div>}
    </div>;
}